import { useState } from 'react'
import MFAccordion from '../ui-components/MFAccordion'
import RenderHTML from '../ui-components/RenderHTML'
import { trackEvent } from '../../utils/appBridge'
import { EventNames } from '../../types/misc.types'

type FAQType = {
    title: string
    descp: string
}

const faqs = [
    {
        "title": "What is Power Rebalancing?",
        "descp": "Power Rebalancing gives you a <strong>completely personalized plan to switch from lagging funds</strong> in your portfolio to the <strong>top-ranked funds</strong>, within the same category and <strong>optimised for tax and exit load.</strong> <br /> <br /> You will only receive a rebalancing plan if we have identified an opportunity in your portfolio which can help you improve your returns meaningfully."
    },
    {
        "title": "Why should I trust the recommendations made?",
        "descp": "We are a SEBI-registered RIA (Registered Investment Advisor), which means our advice is unbiased and built entirely around what’s best for the user. <br/><br /> Our recommendations are built by <strong>inhouse experts and fund analysts</strong> on 20+ years of data and <strong>backtested to deliver superior returns.</strong> <br/> <br />Note : Past success doesn't guarantee future returns and neither do we. But, we can assure you that our experts and portfolio review engine are always on top of your portfolio, doing all the heavy lifting so you don’t have to."
    },
    {
        "title": "Is the rebalancing plan based on past returns or future potential?",
        "descp": `Rebalancing plans are created using PowerUp’s proprietary fund rating and ranking model.<br /> <br /> Our fund rating and ranking model is designed to help investors effortlessly gauge how well a fund is working right now and how confidently it can be held for the future. <br /> <br /> Built by a <strong>highly qualified team of experts and fund analysts</strong> using <strong>20+ years of mutual fund performance data</strong> we evaluate every fund in a category on following parameters: <ul class='list-disc pl-5'> <li><strong>Long term Consistency</strong> (via rolling returns)</li> <li><strong>Volatility </strong>(risk measured in various market conditions)</li> <li> <strong> Recent performance trends </strong> </li> </ul> <br /> <p>Post this every output is <strong>manually validated and fine tuned</strong> by our experts to factor on forward-looking factors like:</p>
<ul class='list-disc pl-5'>
  <li><strong>Fund manager</strong> track record or changes</li>
  <li><strong>Fund strategy</strong> updates</li>
  <li><strong>Fund composition</strong> changes</li>
</ul>
<br />
<p>Our final recommendations are thoroughly back-tested to add maximum value for our users. Backtested results show that our recommendations on rebalancing deliver better returns.</p>`
    },
    {
        "title": "What are missed gains?",
        "descp": `<p>Missed gains represent the <strong>extra returns you could have earned</strong> if your money had been <strong>invested in the top ranked funds</strong> within the same category.</p> <br />
<p>We identify better-performing (from the top ranked funds) alternatives that have consistently outperformed your current fund and compute the difference in INR over the same time period. That difference is what we show as your “missed gains.”</p> <br />
<p>Missed gains is <strong>completely data backed</strong> and <strong>a very exhaustive metric to quantify</strong> the lost opportunity of staying invested in a lagging fund.</p> <br />`
    },
    {
        "title": "How does PowerUp calculate missed gains?",
        "descp": "We simulate your actual investment journey - every SIP, withdrawal, and holding period - and apply it to top-ranked funds in the same category. Then, we compare the current value of your fund with the value of the best-performing alternative. <br /><br /> The difference is what we show as missed gains. Missed gains is <strong>completely data backed</strong> and <strong>a very exhaustive and accurate metric to quantify</strong> the lost opportunity of staying invested in a lagging fund. <br /> <br />Note : Past success doesn't guarantee future returns and neither do we. But, we can assure you that our experts and portfolio review engine are always on top of your portfolio, doing all the heavy lifting so you don’t have to."
    },
    {
        "title": "Why is the Power Rebalancing limited to the same category of mutual funds?",
        "descp": `<p><b><i>Changing categories = changing risk</i></b>. Power Rebalancing plan isn’t about changing your risk profile, it’s about helping you maximize your returns with similar risk.</p> <br />`
    },
    {
        "title": "Why have I not received a rebalancing plan for some of my funds?",
        "descp": `
        <p>We only recommend a switch when we see a clear opportunity to improve your portfolio returns meaningfully. Some common reasons why rebalancing may not be recommended:</p> <br />
        <ol class='list-decimal pl-5'>
  <li>
    <b>Recent investment (&lt;1 year):</b> There isn’t enough performance data yet to make a reliable switch recommendation.
  </li>
  <li>
    <b>Unrated funds on PowerUp:</b> Certain funds (if too new), categories (with &lt;4–5 funds), or non-equity asset classes are currently not rated.
  </li>

<p>
  <i>Note: PowerUp ratings and rankings for Hybrid, Debt, and Solution-oriented funds are launching soon.</i>
</p> 
  <li>
    <b>Your fund is doing well:</b> If the fund is performing well, we want compounding work in your favor instead of disturbing it.
  </li>
  <li>
    <b>Tax/exit load impact:</b> If short-term capital gains (STCG) and/or exit loads outweigh the benefit of switching, we don’t recommend a switch unless absolutely necessary – like in the case if you are holding an Out-of-form Fund.
  </li>
  <li>
    <b>ELSS lock-in:</b> We don’t recommend switches on ELSS investments until they are eligible after the lock-in period.
  </li>
<li>
  <b>Index funds:</b> Returns can differ by almost 1–2% across index funds in a category, which is meaningful. But we currently don’t include them in Power Rebalancing focusing on bigger impact opportunities.
</li>
</ol>`
    },
    {
        "title": "How often will PowerUp provide rebalancing suggestions?",
        "descp": `<p>PowerUp reviews your portfolio every month to ensure it stays aligned with your goals and market conditions.</p> <br />
<p>You’ll receive a rebalancing plan only when there’s a meaningful opportunity, where switching to better-performing, top-ranked funds can improve your returns or reduce risk.</p>`
    },
    {
        "title": "What do the risk profiles mean?",
        "descp": `<p>Your risk profile helps us understand your comfort with risk so we can help you strike the right balance between <b>risk and returns</b> in your portfolio. Here’s what each level means:</p> <br />

<ul class='list-disc pl-5'>
    <li>
        <b>Very Aggressive</b>
        You’re comfortable with very high risk and large ups & downs in the short term, aiming for the highest possible long-term returns.
    </li>
    <li>
        <b>Aggressive</b>
        You’re okay with taking higher risks for potentially higher returns. You can handle short-term volatility without worrying much.
    </li>
    <li>
        <b>Balanced</b>
        You want a balance of growth and stability. You prefer a balance of risk and returns, but with enough cushion for peace of mind.
    </li>
    <li>
        <b>Conservative</b>
        You prefer low risk, steady growth, and protecting your money as it grows.
    </li>
    <li>
        <b>Very Conservative</b>
        Your priority is safety and capital protection. You want minimal risk, even if it means lower returns.
    </li>
</ul>`
    },
    {
        "title": "What does SWP switch mean?",
        "descp": "<p><strong>SWP  is a Systematic Withdrawal Plan</strong>. Instead of withdrawing everything at once, we suggest setting up a SWP to gradually withdraw your investment and reinvest it into the recommended fund. This is done to <strong>spread out the capital movement</strong> to avoid market timing risk and/or high STCG tax implications.</p>"
    },
    {
        "title": "In some scenarios I only see a switch recommendation on my SIP and not my full investment. Why is that?",
        "descp": "<p>If your fund is not performing well and you <strong> have an active SIP going but the case for switching the investment is not strong enough</strong>(maybe due to high exit load, tax implications, or lack of a significantly better alternative). In such cases, we suggest you move your SIPs to a top-ranked fund, so your new investments compound well - like in the case if you are holding an Off-track Fund with an active SIP.</p> <br /><p>This way, you avoid unnecessary costs while still driving your portfolio in the right direction.</p>"
    },
    {
        "title": "What does “High exposure (concentration risk)” mean?",
        "descp": `<p>As a thumb rule, no single fund should dominate your portfolio.</p> <br />
<p>If a <b>single fund accounts for >20% of your portfolio</b>, it creates a <b>big concentration risk</b> and your portfolio returns are overly dependent on one fund house, one fund manager and one investment strategy.</p> <br />
<p>We strongly suggest diversifying into multiple funds of the same category to reduce your concentration risk.</p>`
    },
    {
        "title": "Will I have to pay any tax while rebalancing funds?",
        "descp": `
            <p>All mutual fund investment profits are taxable in India. So you will incur capital gains tax when making a switch as per the rebalancing plan in case you have made any profits.</p> <br />
<p>Power Rebalancing recommendations are <strong>optimized to reduce capital gains tax.</strong></p> <br />
<p>We aim to minimize your tax impact by reducing short-term capital gains (STCG) and favoring long-term capital gains (LTCG) wherever possible. In case we make a switch recommendation which has a STCG then rest assured - we only recommend it when the <strong>potential benefits clearly outweigh the tax cost.</strong></p>
        `
    },
    {
        "title": "Will there be an exit load if I make this switch now?",
        "descp": `<p>Power Rebalancing recommendations are <strong>optimized to minimize Exit Load.</strong></p> <br />
            <p> We aim to minimize your exit load and recommend such switches only and only when the<strong> upside clearly outweighs the exit load payable.</strong></p><br/>
    <p>In case the recommended switch will have an exit load implication, you will be able to see it clearly mentioned in your rebalancing plan, so you can make a fully informed decision before taking any action.</p>`
    }
] as FAQType[]

export const FAQRebalancing = ({ faqHeader = '', threshold = 3 }) => {
    const [accordionState, setAccordionState] = useState<{ [key: string]: boolean }>({})
    // New state to manage showing all vs. a limited number of FAQs
    const [showAll, setShowAll] = useState(false)

    // Determine the list of items to render based on the `showAll` state
    const itemsToDisplay = showAll ? faqs : faqs.slice(0, threshold)

    const toggleShowAll = () => {
        setShowAll((prev) => !prev)
    }

    return (
        <div className='bg-grey80 px-5 pt-5'>
            {faqHeader && <div className='text-[#6A6E6E] text-[13px] pb-2 pl-4'>{faqHeader}</div>}
            <div className='rounded-[12px] flex flex-col gap-[1px] overflow-hidden'>
                {itemsToDisplay?.map(({ title, descp }, index) => {
                    return (
                        <div key={`${title}${index}`} className='bg-white'>
                            <MFAccordion
                                title={title}
                                accordionHeaderClassName='!px-4 !min-h-12'
                                accordionBodyClassName='!px-4 max-w-[90%]'
                                isExpanded={!!accordionState?.[title]}
                                onAccordionClick={() => {
                                    setAccordionState((accordionStatePrev) => {
                                        trackEvent({
                                            eventName: EventNames.UserClicked,
                                            payload: {
                                                eventId: title,
                                                responseName: accordionStatePrev?.[title] ? 'Collapse' : 'Expand',
                                                screenType: 'REBALANCE_PLAN_ISIN'
                                            }
                                        })
                                        return {
                                            ...accordionStatePrev,
                                            [title]: !accordionStatePrev?.[title]
                                        }
                                    })
                                }}
                            >
                                <div className='text-[#6A6E6E] text-[13px] leading-[150%] pb-4'>
                                    <RenderHTML innerHTMLText={descp} />{' '}
                                </div>
                            </MFAccordion>
                        </div>
                    )
                })}
                <div onClick={toggleShowAll} className='bg-white flex py-[16px] px-[20px] justify-between
        cursor-pointer text-[#232323] text-[14px] font-[500] underline'>
                    {showAll ? 'Show less' : "View all FAQ's"}
                </div>
            </div>
        </div>
    )
}